"use client";

import { useState, type FormEvent } from "react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import type { PlaceResult } from "@/lib/api/misc";
import { compareBirthTimes, type BirthTimeSensitivityResponse } from "@/lib/api/rectification";

const fieldClass =
  "w-full rounded-xl border border-border-default bg-surface-base p-3 text-text-primary";

export default function BirthTimeSensitivityExplorer() {
  const [date, setDate] = useState("");
  const [start, setStart] = useState("06:00");
  const [end, setEnd] = useState("07:00");
  const [city, setCity] = useState("");
  const [place, setPlace] = useState<PlaceResult | null>(null);
  const [result, setResult] = useState<BirthTimeSensitivityResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setResult(null);
    if (!place) {
      setError("Select your birth city from the suggestions.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      setResult(
        await compareBirthTimes({
          birth_date: date,
          window_start: start,
          window_end: end,
          latitude: place.latitude,
          longitude: place.longitude,
          timezone: place.timezone,
        })
      );
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "The comparison could not be calculated. Try again."
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section id="compare-birth-times" className="card-sacred my-8 space-y-4 p-5 sm:p-6">
      <h2 className="font-serif text-title font-bold text-text-primary">
        What changes if your birth time is different?
      </h2>
      <p>
        Compare a window of up to six hours on the same day. See the calculated Ascendant, Moon sign
        and birth star at five-minute intervals. No name or mobile number is needed.
      </p>
      <form translate="no" onSubmit={submit} className="space-y-4">
        <fieldset disabled={busy} className="space-y-4">
          <label className="block">
            Birth date
            <input
              type="date"
              required
              min="1900-01-01"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setResult(null);
              }}
              className={fieldClass}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label>
              From
              <input
                type="time"
                required
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                  setResult(null);
                }}
                className={fieldClass}
              />
            </label>
            <label>
              To
              <input
                type="time"
                required
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                  setResult(null);
                }}
                className={fieldClass}
              />
            </label>
          </div>
          <div>
            <label htmlFor="sensitivity-birth-city">Birth city</label>
            <PlaceAutocomplete
              id="sensitivity-birth-city"
              value={city}
              onChange={(value) => {
                setResult(null);
                setCity(value);
                setPlace(null);
              }}
              onSelect={(value) => {
                setResult(null);
                setCity(value.display);
                setPlace(value);
              }}
              required
              placeholder="Search for your birth city"
            />
          </div>
          <button
            type="submit"
            className="min-h-11 w-full rounded-xl bg-surface-brand-strong px-4 py-3 font-semibold text-text-on-brand"
          >
            {busy ? "Calculating…" : "Compare these times"}
          </button>
        </fieldset>
      </form>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      {result && <SensitivityResult result={result} />}
    </section>
  );
}

function SensitivityResult({ result }: { result: BirthTimeSensitivityResponse }) {
  return (
    <div aria-live="polite" className="space-y-4">
      <h3 className="font-semibold">Your comparison · {result.timezone}</h3>
      <p>
        Planet signs unchanged at the sampled times: {result.unchanged_planets.join(", ") || "none"}
        .
      </p>
      <p>
        Planet signs that change: {result.changing_planets.join(", ") || "none in this sample"}.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-body-small">
          <caption className="mb-2 text-left">Local birth times and calculated placements</caption>
          <thead>
            <tr>
              {["Time", "Ascendant", "Moon sign", "Moon nakshatra"].map((label) => (
                <th key={label} scope="col" className="p-2 text-left">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.samples.map((sample) => (
              <tr key={sample.local_time} className="border-t border-border-default">
                <th scope="row" className="p-2 text-left">
                  {sample.local_time}
                </th>
                <td className="p-2">{sample.lagna_rashi}</td>
                <td className="p-2">{sample.moon_rashi}</td>
                <td className="p-2">{sample.moon_nakshatra}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-caption text-text-muted">{result.boundary}</p>
    </div>
  );
}
